import {
  useLayoutEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
} from 'react';
import { Input } from '@/shared/components/ui/input';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import type { Moneda } from './MoneyCell';

/**
 * Currency-formatted text input.
 *
 * **Contract**: parent owns the canonical value as a string of digits
 * with at most one decimal separator (`,` or `.`). This component
 * never injects thousand separators into that canonical value — those
 * are display-only. So `parseMoney(value)` and any downstream
 * arithmetic see the same shape they always did.
 *
 * **Display while focused** (option 3 from the QA): live thousand
 * separators with the AR locale (`2.399.700`) — no `$` prefix, no
 * forced `,00` decimals. The user still types/edits raw digits;
 * separators are inserted on every keystroke and the cursor is
 * pinned to the same digit position so the caret doesn't jump
 * around when separators get re-arranged.
 *
 * **Display while blurred**: full `formatMoney` output
 * (`$2.399.700,00 ARS`).
 *
 * QA #12 + follow-up: amount fields across Pagos read formatted
 * both during edit and at rest. Used by MediosDePagoTable and
 * RegistrarPagoDialog.
 */
type MoneyInputProps = Omit<
  ComponentPropsWithoutRef<typeof Input>,
  'value' | 'onChange' | 'type' | 'inputMode'
> & {
  value: string;
  onChange: (next: string) => void;
  moneda: Moneda;
};

export function MoneyInput({ value, onChange, moneda, ...rest }: MoneyInputProps) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Tracks where to land the caret AFTER the next render. We measure
  // by digit-count rather than by character-index because thousand
  // separators shift around between renders — the digit at position N
  // is stable; the character offset isn't.
  const pendingCaretDigits = useRef<number | null>(null);

  const numeric = parseMoney(value);
  const display = focused
    ? formatForEdit(value)
    : numeric == null
      ? value
      : formatMoney(numeric, moneda, value);

  // Restore caret position after re-render so typing forward / mid-edit
  // doesn't bounce the cursor every time a thousand separator appears
  // or disappears.
  useLayoutEffect(() => {
    if (!focused) return;
    if (pendingCaretDigits.current == null) return;
    const el = inputRef.current;
    if (!el) return;
    const target = positionAfterSignificant(display, pendingCaretDigits.current);
    el.setSelectionRange(target, target);
    pendingCaretDigits.current = null;
  });

  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={display}
      onFocus={(e) => {
        setFocused(true);
        rest.onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        pendingCaretDigits.current = null;
        rest.onBlur?.(e);
      }}
      onChange={(e) => {
        const el = e.target;
        const newRaw = el.value;
        const cursorIdx = el.selectionStart ?? newRaw.length;
        // Capture the caret in canonical-char space BEFORE we
        // sanitize / let React re-render with reformatted separators.
        // We count both digits AND the decimal comma so a freshly
        // typed "," doesn't trap the caret in front of itself.
        pendingCaretDigits.current = countSignificantBefore(newRaw, cursorIdx);
        onChange(sanitizeMoneyEdit(newRaw));
      }}
      {...rest}
    />
  );
}

/**
 * Strip thousand-separator dots (and any other junk) but keep digits
 * and at most one comma. Comma is the AR-locale decimal separator;
 * `parseMoney` handles both shapes on read so the parent never needs
 * to care which one was used.
 */
function sanitizeMoneyEdit(raw: string): string {
  // Drop dots (treated as thousand separators while editing) and any
  // non-digit / non-comma chars.
  let cleaned = raw.replace(/[^\d,]/g, '');
  // Allow at most one comma — keep the first, drop the rest.
  const firstComma = cleaned.indexOf(',');
  if (firstComma >= 0) {
    cleaned =
      cleaned.slice(0, firstComma + 1) +
      cleaned.slice(firstComma + 1).replace(/,/g, '');
  }
  return cleaned;
}

/**
 * Render the canonical value with AR-locale thousand separators.
 * Preserves a trailing comma and any decimal digits the user has
 * typed (so "2399700," displays as "2.399.700," and "2399700,5" as
 * "2.399.700,5"). Doesn't pad to two decimals — that's a display
 * decision for the blurred state, not the editing state.
 */
function formatForEdit(raw: string): string {
  if (!raw) return '';
  // Normalise: dots are thousand separators in the canonical form
  // shouldn't exist (we strip them in sanitize), but old persisted
  // values might use `.` as the decimal separator. Keep at most one
  // `,` OR one trailing `.` as the decimal mark.
  const cleaned = sanitizeMoneyEdit(raw.replace(/\.(?=\d*$)/, ','));
  const [intPart = '', ...rest] = cleaned.split(',');
  const decPart = rest.length > 0 ? rest.join('') : undefined;
  const grouped = intPart
    ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    : '';
  if (decPart === undefined) return grouped;
  return `${grouped},${decPart}`;
}

/**
 * Count "significant" chars in s[0..cursor-1]. Significant = digit
 * (0-9) or the decimal comma. We track both because a freshly typed
 * "," needs to keep the caret on its right side after re-format —
 * counting only digits would put 4 sig chars before the caret in
 * "1.234,|" and 4 digits before position 5 ("1.234|,") in the
 * reformatted output, dropping the caret in front of the comma.
 */
function countSignificantBefore(s: string, cursor: number): number {
  let n = 0;
  const end = Math.min(cursor, s.length);
  for (let i = 0; i < end; i++) {
    const c = s.charCodeAt(i);
    if ((c >= 48 && c <= 57) || c === 44 /* , */) n++;
  }
  return n;
}

/** First index in s such that exactly `count` significant chars sit before it. */
function positionAfterSignificant(s: string, count: number): number {
  if (count <= 0) return 0;
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if ((c >= 48 && c <= 57) || c === 44 /* , */) {
      n++;
      if (n === count) return i + 1;
    }
  }
  return s.length;
}
